const NAME_SUFFIXES = new Set([
    'jr',
    'sr',
    'ii',
    'iii',
    'iv',
    'cna',
    'hha',
    'pca',
    'cma',
    'rn',
    'lpn',
    'lvn',
    'bsn',
    'np',
    'pa',
    'md',
    'dnp',
    'stna',
    'qmap',
]);

function isNameSuffix(part: string): boolean {
    return part
        .split(/[\s/]+/)
        .every((token) =>
            NAME_SUFFIXES.has(token.replace(/\./g, '').toLowerCase()),
        );
}

/**
 * Identity formatter for code that intentionally keeps the stored name
 * order: raw-model helpers, search matching against stored names, and tests.
 * Display code should use `useFormatPersonName()` instead.
 */
export function rawPersonName(name: string | null | undefined): string {
    return name ?? '';
}

// This is dumb, we should migrate to storing first and last names explicitly
// in the database
export function formatCaregiverName(
    fullName: string,
    displayLastFirst: boolean,
): string {
    const trimmed = fullName.trim();
    if (!displayLastFirst || trimmed.length === 0) {
        return trimmed;
    }

    const [head, ...suffixes] = trimmed.split(',').map((part) => part.trim());
    // A comma followed by anything other than a recognized suffix means the
    // name is already stored as Last, First.
    if (suffixes.length > 0 && !suffixes.every(isNameSuffix)) {
        return trimmed;
    }
    const parts = (head ?? '').split(/\s+/);
    if (parts.length === 1) {
        return trimmed;
    }
    const [first, ...rest] = parts;
    const last = rest.join(' ');
    return [`${last}, ${first}`, ...suffixes].join(', ');
}

export function formatPersonNameParts(
    firstName: string | null | undefined,
    lastName: string | null | undefined,
    displayLastFirst: boolean,
): string {
    const first = firstName?.trim() ?? '';
    const last = lastName?.trim() ?? '';
    if (first && last && displayLastFirst) {
        return `${last}, ${first}`;
    }
    return [first, last].filter(Boolean).join(' ');
}

export function compareCaregiverDisplayName(
    nameA: string,
    nameB: string,
    formatName: (s: string) => string,
    sortByLastName = false,
): number {
    const displayA = (
        sortByLastName ? formatCaregiverName(nameA, true) : formatName(nameA)
    ).toLocaleLowerCase();
    const displayB = (
        sortByLastName ? formatCaregiverName(nameB, true) : formatName(nameB)
    ).toLocaleLowerCase();
    if (displayA < displayB) {
        return -1;
    }
    if (displayA > displayB) {
        return 1;
    }
    return 0;
}

export function comparePersonNames(
    nameA: string,
    nameB: string,
    sortByLastName: boolean,
): number {
    const sortNameA = sortByLastName
        ? formatCaregiverName(nameA, true)
        : nameA.trim();
    const sortNameB = sortByLastName
        ? formatCaregiverName(nameB, true)
        : nameB.trim();
    return sortNameA.localeCompare(sortNameB, undefined, {
        sensitivity: 'base',
    });
}
